import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CloseIcon, CreatorCombobox, ErrorState, ExternalIcon, LoadingState, Modal, PlusIcon, useConfirm, useToast } from "@signet/ui";
import { CreatorAvatar, useMe, type CreatorPhotos } from "@signet/web-kit";
import { dayLabel, melbourneDay } from "../shared/week.ts";
import { addAccount, cbKeys, fetchAccounts, fetchCreators, removeAccount, roomUrl, type Account } from "./api.ts";

/* Which Chaturbate usernames are whose. Creators come from Creators; a username is tracked from the moment it is added. */

export function UsernamesPanel({ photos }: { photos: CreatorPhotos }) {
  const { can } = useMe();
  const accounts = useQuery({ queryKey: cbKeys.accounts, queryFn: fetchAccounts });
  const [adding, setAdding] = useState(false);
  const { confirm, element } = useConfirm();
  const list = accounts.data?.accounts ?? [];

  return (
    <section className="panel cb-usernames">
      <div className="panel-head">
        <div>
          <h2>Chaturbate usernames</h2>
          <p>Each one is tracked from the moment it's added. History before that isn't available.</p>
        </div>
        {can("cbstats.account:create") ? (
          <button type="button" className="secondary-button compact" onClick={() => setAdding(true)}>
            <PlusIcon size={13} /> Add username
          </button>
        ) : null}
      </div>
      <div className="panel-body flush">
        {accounts.isLoading ? (
          <LoadingState />
        ) : accounts.isError ? (
          <ErrorState title="Couldn't load the usernames" onRetry={() => accounts.refetch()} />
        ) : !list.length ? (
          <p className="cb-quiet">No usernames yet.</p>
        ) : (
          <div className="table">
            {list.map((a) => (
              <div key={a.id} className="table-row cb-user-row">
                <span className="cb-who">
                  <CreatorAvatar name={a.creator} src={photos(a.creator)} size={26} />
                  <strong>{a.creator}</strong>
                </span>
                <a className="cb-user-link" href={roomUrl(a.username)} target="_blank" rel="noreferrer">
                  {a.username} <ExternalIcon size={11} />
                </a>
                <span className="cb-faint">
                  Added {dayLabel(melbourneDay(a.addedAt))}
                  {a.addedBy ? ` by ${a.addedBy}` : ""}
                </span>
                <span className="cb-faint">{a.lastLive ? `Last live ${dayLabel(melbourneDay(a.lastLive))}` : "Not seen live yet"}</span>
                {can("cbstats.account:delete") ? <RemoveButton account={a} confirm={confirm} /> : <span />}
              </div>
            ))}
          </div>
        )}
      </div>
      {adding ? <AddModal photos={photos} onClose={() => setAdding(false)} /> : null}
      {element}
    </section>
  );
}

function AddModal({ photos, onClose }: { photos: CreatorPhotos; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const creators = useQuery({ queryKey: cbKeys.creators, queryFn: fetchCreators });
  const [creator, setCreator] = useState<string | null>(null);
  const [username, setUsername] = useState("");
  const options = useMemo(() => (creators.data?.creators ?? []).map((name) => ({ id: name, name, avatar: photos(name) })), [creators.data, photos]);
  const add = useMutation({
    mutationFn: () => addAccount({ creator: creator!, username }),
    onSuccess: ({ account }) => {
      void queryClient.invalidateQueries({ queryKey: cbKeys.all });
      toast.success("Added", `${account.username} is tracked for ${account.creator} from now on.`);
      onClose();
    },
    onError: (error: Error) => toast.error("Couldn't add", error.message),
  });
  const ready = !!creator && username.trim().length > 1 && !add.isPending;
  return (
    <Modal
      open
      title="Add a Chaturbate username"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="text-button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="button brass" disabled={!ready} onClick={() => add.mutate()}>
            Add
          </button>
        </>
      }
    >
      <form
        className="cb-add"
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) add.mutate();
        }}
      >
        <CreatorCombobox creators={options} value={creator} onChange={setCreator} allowNone={false} />
        <label className="field">
          <span className="label">Username or room link</span>
          <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="dreamyylilah or https://chaturbate.com/dreamyylilah/" autoComplete="off" spellCheck={false} />
        </label>
        <p className="cb-faint">Creators come from Creators. Add or bring someone back there and they appear here.</p>
      </form>
    </Modal>
  );
}

function RemoveButton({ account, confirm }: { account: Account; confirm: ReturnType<typeof useConfirm>["confirm"] }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const remove = useMutation({
    mutationFn: () => removeAccount(account.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cbKeys.all });
      toast.success("Taken off", `${account.username} is no longer tracked.`);
    },
    onError: (error: Error) => toast.error("Couldn't remove", error.message),
  });
  return (
    <button
      type="button"
      className="icon-button ghost small"
      aria-label={`Stop tracking ${account.username}`}
      onClick={async () => {
        if (await confirm({ title: `Stop tracking ${account.username}?`, body: `It comes off ${account.creator}'s CB Stats. Streams already tracked are kept.`, confirmLabel: "Stop tracking", danger: true })) remove.mutate();
      }}
    >
      <CloseIcon size={12} />
    </button>
  );
}
